// DierMatch public site — pure, React-free route helpers (unit-testable without a DOM).
//
// The public adoption site lives under `/dieren` (overview) and `/dieren/<slug>` (one animal). Like
// the nazorg portal it is a standalone, unauthenticated landing: it must NOT run the auth gate (no
// silent restore, no login redirect). Navigation between overview and detail is client-side
// (history pushState), and a deep link straight to `/dieren/<slug>` resolves to that detail.
//
// De eigen pagina van één opvang staat onder `/dieren/opvang/<sleutel>/<naam>`: dezelfde vorm als op
// ikzoekbaas (story 8-8), zodat een asiel voor beide sites dezelfde soort link krijgt.

import { naamSlug } from "./ikzoekbaasRoute";
import { isDierMatchHost } from "./publicSite";

const DIERMATCH_PREFIX = "/dieren";

/**
 * Padsegmenten direct onder `/dieren` die geen dierslug zijn.
 *
 * <p>`opvang` was de eerste, `match` (de AI-matchwizard, epic 12) de tweede, `favorieten` de
 * derde. Zonder deze lijst
 * leest `readSlugFromPath("/dieren/match")` een dier met slug `match` en toont de site "Dit dier is
 * niet (meer) beschikbaar" — een pagina die bestaat, maar de verkeerde.
 *
 * <p>Let op: dit is de cliëntkant. `PublicationSlugs.uniqueSlug` kan een dier dat "Match" heet nog
 * steeds deze slug geven; die pagina is dan onbereikbaar. Zeldzaam en zichtbaar, en het hoort aan
 * de serverkant opgelost te worden (dezelfde woordenlijst bij het munten van een slug) — genoteerd
 * bij epic 12, niet stil gelaten.
 */
const GERESERVEERD = new Set(["opvang", "match", "favorieten"]);

/** True when the path is the DierMatch site (`/dieren` or `/dieren/<slug>`). */
export function isDierMatchPath(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return false;
  }
  const path = pathname.replace(/\/+$/, "");
  return path === DIERMATCH_PREFIX || path.startsWith(`${DIERMATCH_PREFIX}/`);
}

function decode(raw: string): string {
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.trim();
  }
}

/**
 * De sleutel uit een `/dieren/opvang/<sleutel>/<naam>`-pad, of `""` als het geen opvangpagina is.
 *
 * <p>Alleen de sleutel lost op; wat erachter staat is de naam, puur voor de leesbaarheid, en wordt
 * genegeerd. Zo blijft een link op de site van een asiel werken als iemand de opvang hernoemt.
 */
export function readOpvangFromPath(pathname: string | null | undefined): string {
  if (!pathname) {
    return "";
  }
  const match = pathname.replace(/\/+$/, "").match(/^\/dieren\/opvang\/([^/]+)(?:\/.*)?$/);
  return match ? decode(match[1]) : "";
}

/**
 * Extract the slug from a `/dieren/<slug>` path. Returns the trimmed slug, or `""` for the overview
 * (`/dieren`) and for an opvang page. Takes the pathname as an argument (not `location`) so it is
 * testable in isolation.
 */
export function readSlugFromPath(pathname: string | null | undefined): string {
  if (!pathname) {
    return "";
  }
  const match = pathname.replace(/\/+$/, "").match(/^\/dieren\/(.+)$/);
  if (!match) {
    return "";
  }
  const eerste = match[1].split("/")[0];
  return GERESERVEERD.has(decode(eerste).toLowerCase()) ? "" : decode(match[1]);
}

/** True voor `/dieren/match` — de ingang van de AI-matchwizard (epic 12). */
export function isMatchPath(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return false;
  }
  return /^\/dieren\/match$/i.test(pathname.replace(/\/+$/, ""));
}

/** De deelbare link naar de matchwizard. */
export function matchPath(): string {
  return `${DIERMATCH_PREFIX}/match`;
}

/** True voor `/dieren/favorieten` — de bewaarde dieren van deze bezoeker. */
export function isFavorietenPath(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return false;
  }
  return /^\/dieren\/favorieten$/i.test(pathname.replace(/\/+$/, ""));
}

/**
 * De link naar de favorieten. Deelbaar is hij niet echt — de lijst staat in de browser van deze
 * bezoeker — maar hij hoort wel een eigen adres te hebben, zodat terug ook hier werkt.
 */
export function favorietenPath(): string {
  return `${DIERMATCH_PREFIX}/favorieten`;
}

/** The canonical overview is the root on diermatch.nl and `/dieren` on the shared app host. */
export function overviewPath(hostname?: string | null): string {
  return isDierMatchHost(hostname) ? "/" : DIERMATCH_PREFIX;
}

/** Detail links keep the established `/dieren/<slug>` shape on both entrances. */
export function detailPath(slug: string): string {
  return `${DIERMATCH_PREFIX}/${encodeURIComponent(slug)}`;
}

/** De deelbare link naar het aanbod van één opvang — ook op diermatch.nl onder `/dieren`. */
export function opvangPath(sleutel: string, naam?: string | null): string {
  const basis = `${DIERMATCH_PREFIX}/opvang/${encodeURIComponent(sleutel)}`;
  const slug = naamSlug(naam);
  return slug ? `${basis}/${slug}` : basis;
}
